import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useIsOwner } from '@/auth/role';
import { IconBook, IconClose, IconKey, IconMenu, IconReceipt, IconStore, IconUsers } from '@/components/Icons';
import { LogoMark } from '@/components/Logo';
import { UserMenu } from '@/components/UserMenu';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  /** Hidden from an ISSUER login, which cannot use the page. */
  ownerOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/cp', label: 'Tenants', icon: <IconStore className="h-4 w-4" />, end: true },
  { to: '/cp/licenses', label: 'Licences', icon: <IconKey className="h-4 w-4" /> },
  { to: '/cp/plans', label: 'Plans', icon: <IconReceipt className="h-4 w-4" />, ownerOnly: true },
  { to: '/cp/audit', label: 'Audit log', icon: <IconBook className="h-4 w-4" />, ownerOnly: true },
  { to: '/cp/console-users', label: 'Console users', icon: <IconUsers className="h-4 w-4" />, ownerOnly: true },
];

function navItemClass({ isActive }: { isActive: boolean }): string {
  const base =
    'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
  return isActive
    ? `${base} bg-brand-600/[0.12] text-brand-700`
    : `${base} text-slate-700 hover:bg-slate-200 hover:text-slate-900`;
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const isOwner = useIsOwner();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    setSigningOut(true);
    void logout().finally(() => {
      setSigningOut(false);
      navigate('/cp/login', { replace: true });
    });
  }, [logout, navigate]);

  const sidebar = (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <LogoMark className="h-9 w-9" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">SelloKart</p>
          <p className="truncate text-xs text-slate-500">Cloud console</p>
        </div>
      </div>

      <nav aria-label="Main" className="flex-1 space-y-1 px-3 py-2">
        {NAV_ITEMS.filter((item) => isOwner || !item.ownerOnly).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={navItemClass}>
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* The account block and Sign out live in the top-right app bar, where
          users expect them — see <UserMenu>. */}
    </div>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-slate-200 md:block">{sidebar}</aside>

      {/* Mobile drawer */}
      {menuOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 animate-fade-in bg-slate-900/50" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 shadow-xl">
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="btn btn-ghost absolute right-2 top-3 h-8 w-8 p-0 text-slate-500 hover:bg-slate-200 hover:text-slate-900"
              aria-label="Close menu"
            >
              <IconClose className="h-4 w-4" />
            </button>
            {sidebar}
          </div>
        </div>
      ) : null}

      <div className="md:pl-64">
        {/* App bar: on mobile it also carries the drawer toggle and wordmark;
            on desktop it exists for the account menu at the right. */}
        <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="btn btn-ghost h-9 w-9 p-0 md:hidden"
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            <IconMenu className="h-5 w-5" />
          </button>
          <LogoMark className="h-7 w-7 md:hidden" />
          <span className="text-sm font-semibold text-slate-900 md:hidden">SelloKart Cloud</span>

          <div className="ml-auto">
            <UserMenu
              name={user?.username ?? (isOwner ? 'Super admin' : 'Issuer')}
              email={user?.email}
              onSignOut={handleLogout}
              signingOut={signingOut}
            />
          </div>
        </div>

        <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
